import { z } from 'zod';

/** Optional HTTPS URL for lesson/program cover images (empty clears). */
export const optionalImageUrlSchema = z
  .union([z.string().url(), z.literal('')])
  .optional()
  .transform((v) => (v === '' || v === undefined ? undefined : v));

/**
 * Form/API times may be HH:MM (inputs) or HH:MM:SS (e.g. Postgres TIME).
 * Normalizes to HH:MM for consistent validation and inferred types.
 */
export const lessonTimeHHMMSchema = z
  .string()
  .refine((s) => /^\d{1,2}:\d{2}(:\d{2})?$/.test(s.trim()), 'Invalid time format (HH:MM)')
  .transform((s) => {
    const m = s.trim().match(/^(\d{1,2}):(\d{2})(?::(\d{2}))?$/);
    if (!m) return s.trim();
    return `${m[1].padStart(2, '0')}:${m[2]}`;
  });

const baseLessonFormSchema = z.object({
  lessonType: z.enum(['group', 'private', 'camp']),
  termIds: z.array(z.string()).optional(),
  startDate: z.string().optional(),
  endDate: z.string().optional(),
  numberOfSessions: z.number().optional(),
  courtIds: z.array(z.string()),
  coachIds: z.array(z.string()).min(1, 'At least one coach is required'),
  locationId: z.union([z.string().uuid(), z.literal('')]).nullable().optional(),
  name: z.string().min(1, 'Lesson name is required').max(100, 'Name too long'),
  description: z.string().optional(),
  skillLevel: z.enum(['any', 'beginner', 'intermediate', 'advanced']),
  ageGroup: z.enum(['juniors', 'adults', 'all']),
  daysOfWeek: z.array(z.number().min(0).max(6)).min(1, 'At least one day is required'),
  startTime: lessonTimeHHMMSchema,
  endTime: lessonTimeHHMMSchema,
  maxCapacity: z.number().min(-1, 'Capacity must be at least 1 or -1 for no limit').max(50, 'Maximum 50 students').optional(),
  price: z.number().min(0, 'Price cannot be negative'),
  pricePerSession: z.number().min(0, 'Price per session cannot be negative').optional(),
  pricingType: z.enum(['total', 'per_session']).optional(),
  campDayPrice: z.number().min(0).optional(),
  campFullPrice: z.number().min(0).optional(),
  sessionPrice: z.number().min(0).nullable().optional(),
  isActive: z.boolean(),
  isFeatured: z.boolean().optional(),
  ballColor: z.enum(['blue', 'red', 'orange', 'green', 'yellow']).nullable().optional().or(z.literal('')),
  ageGroupId: z.union([z.string().uuid(), z.literal('')]).optional().nullable(),
  academySportId: z.string().uuid().optional(),
  /** Link lesson to a development program (Professional+) */
  programId: z.string().uuid().optional().nullable(),
  imageUrl: optionalImageUrlSchema,
  /** Standalone private package: explicit session calendar days (YYYY-MM-DD). */
  privateSessionDates: z.array(z.string().regex(/^\d{4}-\d{2}-\d{2}$/)).optional(),
  /** Camp: explicit camp day calendar (YYYY-MM-DD); form derives startDate/endDate/daysOfWeek. */
  campSessionDates: z.array(z.string().regex(/^\d{4}-\d{2}-\d{2}$/)).optional(),
});

export const lessonFormSchema = baseLessonFormSchema
  .refine((data) => {
    if (data.lessonType === 'private') {
      const hasTerm = !!(data.termIds && data.termIds.length > 0);
      if (hasTerm) {
        return data.termIds!.length === 1;
      }
      const explicitDates = data.privateSessionDates?.filter(Boolean) ?? [];
      if (explicitDates.length > 0) return true;
      return !!data.startDate && !!data.numberOfSessions && data.numberOfSessions > 0;
    }
    if (data.lessonType === 'camp') {
      return (
        !!data.startDate &&
        !!data.endDate &&
        !!data.maxCapacity &&
        data.campDayPrice != null &&
        data.campFullPrice != null &&
        (!data.termIds || data.termIds.length === 0)
      );
    }
    return !!(data.termIds && data.termIds.length > 0 && data.maxCapacity);
  }, {
    message:
      'Standalone private lessons need at least one session date, or a start date and session count. Private lessons in a program need one program selected. Camps require start/end dates, capacity, day price and full camp price (no term). Group lessons require at least one term and capacity.',
    path: ['termIds'],
  })
  .refine(
    (data) => {
      if (!data.startTime || !data.endTime) return true;
      const start = new Date(`2000-01-01T${data.startTime}`);
      const end = new Date(`2000-01-01T${data.endTime}`);
      return end > start;
    },
    { message: 'End time must be after start time', path: ['endTime'] }
  );

export type LessonFormData = z.infer<typeof lessonFormSchema>;

const explicitSessionSlotSchema = z.object({
  sessionDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'sessionDate must be YYYY-MM-DD'),
  startTime: lessonTimeHHMMSchema,
  endTime: lessonTimeHHMMSchema,
});

/** @deprecated Use explicitSessionSlotSchema */
const explicitPrivateSessionSlotSchema = explicitSessionSlotSchema;

export const lessonCreateRequestSchema = baseLessonFormSchema
  .extend({
    academyId: z.string().min(1, 'Academy ID is required'),
    currency: z.enum(['USD', 'AUD', 'GBP']).optional(),
    /** When set, lesson_sessions rows are created exactly from these slots (private lessons only). */
    explicitPrivateSessions: z.array(explicitSessionSlotSchema).optional(),
    /** When set, lesson_sessions rows are created exactly from these slots (camp lessons only). */
    explicitCampSessions: z.array(explicitSessionSlotSchema).optional(),
  })
  .refine(
    (data) => {
      if (data.lessonType === 'private' && data.courtIds.length === 0) {
        return true;
      }
      return data.courtIds.length >= 1;
    },
    { message: 'At least one court is required', path: ['courtIds'] }
  )
  .refine((data) => {
    if (data.lessonType === 'private') {
      const hasTerm = !!(data.termIds && data.termIds.length > 0);
      if (hasTerm) {
        return data.termIds!.length === 1;
      }
      const explicitDates = data.privateSessionDates?.filter(Boolean) ?? [];
      if (explicitDates.length > 0) return true;
      return !!data.startDate && !!data.numberOfSessions && data.numberOfSessions > 0;
    }
    if (data.lessonType === 'camp') {
      return (
        !!data.startDate &&
        !!data.endDate &&
        !!data.maxCapacity &&
        data.campDayPrice != null &&
        data.campFullPrice != null &&
        (!data.termIds || data.termIds.length === 0)
      );
    }
    return !!(data.termIds && data.termIds.length > 0 && data.maxCapacity);
  }, {
    message:
      'Standalone private lessons need at least one session date, or a start date and session count. Private lessons in a program need one program selected. Camps require start/end dates, capacity, day price and full camp price (no term). Group lessons require at least one term and capacity.',
    path: ['termIds'],
  })
  .refine((data) => {
    if (!data.startTime || !data.endTime) return true;
    const start = new Date(`2000-01-01T${data.startTime}`);
    const end = new Date(`2000-01-01T${data.endTime}`);
    return end > start;
  }, {
    message: 'End time must be after start time',
    path: ['endTime'],
  })
  .refine(
    (data) => {
      if (!data.explicitPrivateSessions?.length) return true;
      if (data.lessonType !== 'private') return false;
      return data.numberOfSessions === data.explicitPrivateSessions.length;
    },
    {
      message: 'explicitPrivateSessions must match numberOfSessions for private lessons',
      path: ['explicitPrivateSessions'],
    }
  )
  .refine(
    (data) => {
      if (!data.explicitPrivateSessions?.length) return true;
      return data.explicitPrivateSessions.every((row) => {
        const start = new Date(`2000-01-01T${row.startTime}`);
        const end = new Date(`2000-01-01T${row.endTime}`);
        return end > start;
      });
    },
    { message: 'Each session end time must be after start time', path: ['explicitPrivateSessions'] }
  );

export type LessonCreateRequest = z.infer<typeof lessonCreateRequestSchema>;

/** Partial lesson fields accepted by PUT /lessons/:id (validated server-side with existing lesson context). */
export const lessonUpdateRequestSchema = baseLessonFormSchema
  .partial()
  .extend({
    termId: z.string().uuid().nullable().optional(),
    termIds: z.array(z.string().uuid()).max(1).optional(),
    explicitPrivateSessions: z.array(explicitSessionSlotSchema).optional(),
    explicitCampSessions: z.array(explicitSessionSlotSchema).optional(),
    privateSessionsExplicit: z.boolean().optional(),
    campSessionsExplicit: z.boolean().optional(),
    isActive: z.boolean().optional(),
  })
  .passthrough();

/** Create multiple camp weeks in one request (each week is an independent camp lesson). */
export const campSeriesCreateRequestSchema = lessonCreateRequestSchema.and(
  z.object({
    weeks: z.number().int().min(1).max(52),
  })
);

export type CampSeriesCreateRequest = z.infer<typeof campSeriesCreateRequestSchema>;

export type LessonUpdateRequest = z.infer<typeof lessonUpdateRequestSchema>;

/** POST /lessons/:id/coaches — append a coach to the lesson default roster. */
export const addLessonCoachSchema = z.object({
  academyId: z.string().uuid('Invalid academy ID'),
  coachId: z.string().uuid('Invalid coach ID'),
  notifyCoachEmail: z.boolean().default(true),
});

export type AddLessonCoachRequest = z.infer<typeof addLessonCoachSchema>;
